// Replacement and age of an endpoint device, read from the result of the system info script (Get-SystemInfo), which
// reports the machine's hardware and system details, an estimate of its age (DeviceAge) and a replacement verdict
// (ReplacementAssessment). Plain functions only (no React), so they can be unit tested.

export const SYSINFO_SCRIPT = 'get-systeminfo';
export const isSystemInfoScript = (name) => String(name || '').toLowerCase() === SYSINFO_SCRIPT;

// A machine older than this many years is listed for replacement, whatever its verdict says.
export const AGE_LIMIT_YEARS = 4;

// The fields read for every device (VES fields=...): enough for the tiles and the machine list. A selected path comes
// back under its dotted name, e.g. results['DeviceAge.estimatedAgeYears']. A machine's whole result is loaded when its
// own page is opened.
export const SYSINFO_FIELDS = [
  'ComputerName', 'Manufacturer', 'Model', 'SerialNumber', 'OperatingSystem', 'WindowsDisplayVersion',
  'Processor', 'MemoryInstalledGB', 'ReportDate',
  'DeviceAge.estimatedAgeYears', 'DeviceAge.estimatedBuildDate', 'DeviceAge.ageSource',
  'ReplacementAssessment.verdict', 'ReplacementAssessment.verdictReasons', 'ReplacementAssessment.chassis',
  'ReplacementAssessment.dates.replacementDueDate',
];

export const VERDICT = { REPLACE_NOW: 'replaceNow', PLAN: 'planReplacement', HEALTHY: 'healthy' };

export const VERDICT_LABELS = {
  [VERDICT.REPLACE_NOW]: 'Replace now',
  [VERDICT.PLAN]: 'Plan replacement',
  [VERDICT.HEALTHY]: 'Healthy',
};

// MUI colour per verdict; anything else stays neutral.
export const VERDICT_COLORS = {
  [VERDICT.REPLACE_NOW]: 'error',
  [VERDICT.PLAN]: 'warning',
  [VERDICT.HEALTHY]: 'success',
};

// The verdict of a machine, or null when it has not reported one (the script has not run in its new version yet).
export const verdictOf = (item) => {
  const raw = item?.results?.['ReplacementAssessment.verdict'];
  return raw === null || typeof raw === 'undefined' || String(raw).trim() === '' ? null : String(raw).trim();
};

// Estimated age in years, or null when unknown. (A missing value must not read as 0 years.)
export const ageYearsOf = (item) => {
  const raw = item?.results?.['DeviceAge.estimatedAgeYears'];
  if (raw === null || typeof raw === 'undefined' || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const isReplaceNow = (item) => verdictOf(item) === VERDICT.REPLACE_NOW;
export const isPlanReplacement = (item) => verdictOf(item) === VERDICT.PLAN;
export const isOlderThanLimit = (item) => {
  const age = ageYearsOf(item);
  return age !== null && age > AGE_LIMIT_YEARS;
};

// Machines to replace, plan to replace, or older than the age limit.
export const needsReplacement = (item) => isReplaceNow(item) || isPlanReplacement(item) || isOlderThanLimit(item);

// A machine that has reported neither a verdict nor an age.
export const isNotAssessed = (item) => verdictOf(item) === null && ageYearsOf(item) === null;

// A machine that passed the assessment and is not older than the limit.
export const isHealthy = (item) => verdictOf(item) === VERDICT.HEALTHY && !isOlderThanLimit(item);

// The reasons of a verdict as one line ("; " between them). The field comes back as an array, or as text.
export const reasonsText = (item) => {
  const raw = item?.results?.['ReplacementAssessment.verdictReasons'];
  if (Array.isArray(raw)) return raw.filter(Boolean).join('; ');
  return raw ? String(raw) : '';
};

export const verdictLabel = (item) => {
  const verdict = verdictOf(item);
  return verdict ? (VERDICT_LABELS[verdict] || verdict) : '';
};
